import { useState, useEffect } from 'react';
import './Admin.css';

function authFetch(url, token, options = {}) {
  return fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
}

const SOURCE_TYPES = [
  {
    id: 'documents',
    label: 'Documents (Ingestible)',
    desc: 'Upload and index PDF, Markdown, and other documents',
    fields: [
      { key: 'url', label: 'Service URL', type: 'text', placeholder: 'http://localhost:8082' },
    ],
  },
  {
    id: 'axiom',
    label: 'Knowledge Base (Axiom)',
    desc: 'NyxCore Axiom RAG for semantic document search',
    fields: [
      { key: 'token', label: 'Access Token', type: 'password', placeholder: 'axiom token' },
    ],
  },
  {
    id: 'repos',
    label: 'Repositories (CKB)',
    desc: 'Code knowledge base for codebase understanding',
    fields: [],
  },
];

export default function SourcesConfig({ token }) {
  const [configs, setConfigs] = useState({
    documents: { enabled: false, url: '' },
    axiom: { enabled: false, token: '' },
    repos: { enabled: false, repositories: [] },
  });
  const [newRepo, setNewRepo] = useState({ name: '', path: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState({});
  const [testLoading, setTestLoading] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [deps, setDeps] = useState(null);
  const [pipInstalling, setPipInstalling] = useState(false);
  const [pipOutput, setPipOutput] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const [sourcesRes, depsRes] = await Promise.all([
          authFetch('/api/admin/sources', token),
          fetch('/api/setup/deps'),
        ]);
        if (sourcesRes.ok) {
          const data = await sourcesRes.json();
          setConfigs(data.sources);
        }
        if (depsRes.ok) setDeps(await depsRes.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  async function handlePipInstall() {
    setPipInstalling(true);
    setPipOutput('');
    try {
      const res = await authFetch('/api/admin/sources/documents/pip-install', token, { method: 'POST' });
      const data = await res.json();
      setPipOutput(data.output || '');
      if (data.ok) setDeps(d => ({ ...d, ingestible: true }));
    } catch (err) {
      setPipOutput(err.message);
    } finally {
      setPipInstalling(false);
    }
  }

  function updateConfig(type, field, value) {
    setConfigs(c => ({ ...c, [type]: { ...c[type], [field]: value } }));
  }

  async function handleTest(type) {
    setTestLoading(t => ({ ...t, [type]: true }));
    setTestResults(r => ({ ...r, [type]: null }));
    try {
      const payload = type === 'documents'
        ? { url: configs.documents.url }
        : type === 'axiom'
        ? { token: configs.axiom.token }
        : {};
      const res = await authFetch(`/api/admin/sources/${type}/test`, token, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      setTestResults(r => ({ ...r, [type]: res.ok ? 'ok' : (data.message || 'Connection failed') }));
    } catch (err) {
      setTestResults(r => ({ ...r, [type]: err.message }));
    } finally {
      setTestLoading(t => ({ ...t, [type]: false }));
    }
  }

  async function saveRepos(repositories, currentEnabled) {
    setSaveStatus(s => ({ ...s, repos: 'saving' }));
    try {
      const res = await authFetch('/api/admin/sources/repos', token, {
        method: 'PUT',
        body: JSON.stringify({ enabled: currentEnabled, repositories }),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveStatus(s => ({ ...s, repos: 'saved' }));
      setTimeout(() => setSaveStatus(s => ({ ...s, repos: null })), 2000);
    } catch {
      setSaveStatus(s => ({ ...s, repos: 'error' }));
    }
  }

  async function addRepo() {
    const name = newRepo.name.trim();
    const path = newRepo.path.trim();
    if (!name || !path) return;
    if (saveStatus.repos === 'saving') return;

    const existing = configs.repos.repositories || [];
    if (existing.some(r => r.path === path)) return; // dedup by path

    const repo = { id: crypto.randomUUID(), name, path };
    const updated = [...existing, repo];
    setConfigs(c => ({ ...c, repos: { ...c.repos, repositories: updated } }));
    setNewRepo({ name: '', path: '' });
    await saveRepos(updated, configs.repos.enabled);
  }

  async function removeRepo(id) {
    const existing = configs.repos.repositories || [];
    const updated = existing.filter(r => r.id !== id);
    setConfigs(c => ({ ...c, repos: { ...c.repos, repositories: updated } }));
    await saveRepos(updated, configs.repos.enabled);
  }

  async function handleSave(type) {
    setSaveStatus(s => ({ ...s, [type]: 'saving' }));
    try {
      const res = await authFetch(`/api/admin/sources/${type}`, token, {
        method: 'PUT',
        body: JSON.stringify(configs[type]),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveStatus(s => ({ ...s, [type]: 'saved' }));
      setTimeout(() => setSaveStatus(s => ({ ...s, [type]: null })), 2000);
    } catch {
      setSaveStatus(s => ({ ...s, [type]: 'error' }));
    }
  }

  if (loading) return <div className="admin-section"><p style={{ color: 'var(--text-tertiary)' }}>Loading...</p></div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Sources</h2>
        <p className="admin-section-desc">Configure the knowledge sources available to Etch.</p>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="sources-list">
        {SOURCE_TYPES.map(({ id, label, desc, fields }) => {
          const cfg = configs[id] || {};
          const testResult = testResults[id];
          return (
            <div key={id} className="source-config-card">
              <div className="source-config-header">
                <div className="source-config-info">
                  <div className="source-config-toggle">
                    <div
                      className={`toggle-switch ${cfg.enabled ? 'on' : ''}`}
                      onClick={() => updateConfig(id, 'enabled', !cfg.enabled)}
                      role="switch"
                      tabIndex={0}
                      onKeyDown={e => e.key === ' ' && updateConfig(id, 'enabled', !cfg.enabled)}
                    >
                      <div className="toggle-knob" />
                    </div>
                    <div>
                      <div className="source-config-name">
                        {label}
                        {testResult && (
                          <span className={`status-dot ${testResult === 'ok' ? 'success' : 'error'}`} />
                        )}
                      </div>
                      <div className="source-config-desc">{desc}</div>
                    </div>
                  </div>
                </div>
                <div className="source-config-actions">
                  {fields.length > 0 && cfg.enabled && (
                    <button
                      className="btn-sm btn-ghost"
                      onClick={() => handleTest(id)}
                      disabled={testLoading[id]}
                    >
                      {testLoading[id] ? 'Testing…' : 'Test'}
                    </button>
                  )}
                  <button
                    className="btn-sm btn-primary"
                    onClick={() => handleSave(id)}
                    disabled={saveStatus[id] === 'saving'}
                  >
                    {saveStatus[id] === 'saving' ? 'Saving…'
                      : saveStatus[id] === 'saved' ? 'Saved!'
                      : saveStatus[id] === 'error' ? 'Error'
                      : 'Save'}
                  </button>
                </div>
              </div>

              {id === 'documents' && deps?.ingestible === false && (
                <div className="pip-banner">
                  <span className="pip-banner-text">
                    Ingestible is not installed. Install it to enable document ingestion.
                  </span>
                  <button
                    className="btn-sm btn-primary"
                    onClick={handlePipInstall}
                    disabled={pipInstalling}
                  >
                    {pipInstalling ? 'Installing…' : 'pip install ingestible'}
                  </button>
                </div>
              )}
              {id === 'documents' && deps?.ingestible === true && (
                <div className="dep-ok">Ingestible installed</div>
              )}
              {id === 'documents' && pipOutput && (
                <pre className="pip-output">{pipOutput}</pre>
              )}
              {id === 'repos' && deps?.ckb === false && (
                <div className="pip-banner">
                  <span className="pip-banner-text">
                    CKB not found — run <code>npx @tastehub/ckb init && npx @tastehub/ckb index</code> in your workspace first.
                  </span>
                </div>
              )}
              {id === 'repos' && deps?.ckb === true && (
                <div className="dep-ok">CKB available</div>
              )}
              {id === 'repos' && cfg.enabled && (
                <div className="source-config-fields">
                  <div className="repos-list">
                    {(cfg.repositories || []).length === 0 && (
                      <p className="repos-empty">No repositories configured.</p>
                    )}
                    {(cfg.repositories || []).map(repo => (
                      <div key={repo.id} className="repo-row">
                        <div className="repo-info">
                          <span className="repo-name">{repo.name}</span>
                          <span className="repo-path">{repo.path}</span>
                        </div>
                        <button
                          className="btn-sm btn-ghost btn-danger"
                          onClick={() => removeRepo(repo.id)}
                          disabled={saveStatus.repos === 'saving'}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="repo-add-row">
                    <input
                      type="text"
                      placeholder="Name (e.g. my-service)"
                      value={newRepo.name}
                      onChange={e => setNewRepo(r => ({ ...r, name: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && document.getElementById('repo-path-input').focus()}
                    />
                    <input
                      id="repo-path-input"
                      type="text"
                      placeholder="Absolute path (e.g. /home/user/my-service)"
                      value={newRepo.path}
                      onChange={e => setNewRepo(r => ({ ...r, path: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && addRepo()}
                    />
                    <button
                      className="btn-sm btn-primary"
                      onClick={addRepo}
                      disabled={saveStatus.repos === 'saving' || !newRepo.name.trim() || !newRepo.path.trim()}
                    >
                      {saveStatus.repos === 'saving' ? 'Saving…' : 'Add'}
                    </button>
                  </div>
                </div>
              )}

              {cfg.enabled && fields.length > 0 && (
                <div className="source-config-fields">
                  {fields.map(({ key, label: fLabel, type, placeholder }) => (
                    <div key={key} className="admin-form-row">
                      <label>{fLabel}</label>
                      <input
                        type={type}
                        value={cfg[key] || ''}
                        onChange={e => updateConfig(id, key, e.target.value)}
                        placeholder={placeholder}
                      />
                    </div>
                  ))}
                  {testResult && (
                    <div className={`test-result-inline ${testResult === 'ok' ? 'ok' : 'fail'}`}>
                      {testResult === 'ok' ? 'Connection successful' : testResult}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
